import moment from 'moment';
import { useMemo, useState, useEffect, useRef } from 'react';

import { Tooltip } from '@material-ui/core';

import { downloadFile, httpDelete, httpPost } from '../utils';
import xExitIcon from '../common/NewImages/x-exit.svg';
import fileMediumIcon from '../common/NewImages/file-medium.svg';
import trashMediumIcon from '../common/NewImages/trash-medium.svg';
import refreshIcon from '../common/NewImages/refresh.svg';
import downloadIcon from '../common/NewImages/download.svg';
import loadingDarkerIcon from '../common/NewImages/loading-darker.svg';
import vCircleIcon from '../common/NewImages/v-circle.svg';
import undoIcon from '../common/NewImages/undo.svg';
import noLogsIcon from '../common/NewImages/no-logs.svg';
import AdminConsoleIcon from '../common/NewImages/admin-console.svg';
import vIcon from '../common/NewImages/v-icon.svg';
import { TOWER_ID } from '@silverfort-nodelibs/common/utils/consts';
import useClickOutside from '../DropdownMenu/useClickOutside';

const TOOLTIP_STYLING =
    'my-2 px-3 h-8 flex items-center text-sf font-medium bg-sf-gray-800 radius border border-sf-gray-700 shadow-sf-small';

const LOG_ACTION_TYPE = {
    GENERATED: 'Generated',
    SENT: 'Sent',
    SEND_ALL: 'All Components'
};
const SERVERS_LOG_TYPES = {
    PUSH_SERVICE: 'Push Service',
    NODE: 'Node',
    DOMAIN_CONTROLLER: 'Domain Controller',
    ADMIN_CONSOLE: 'Admin Console'
};

const parseDateStr = (dateStr) => {
    if (dateStr) {
        return moment(dateStr).valueOf();
    }
};

const parseMillisecondsDate = (date) => {
    const localDate = moment(date).local();
    return `${localDate.format('L')}, ${localDate.format('LTS')}`;
};

const LogDetails = ({ logAction, icon }) => {
    return (
        <div className="flex">
            <img className="pr-2" src={icon} alt="" />
            <div>
                <div className="flex items-center">
                    {logAction.name.length > 20 ? (
                        <Tooltip
                            title={logAction.name}
                            enterDelay={1000}
                            enterNextDelay={1000}
                            placement={'top'}
                            classes={{
                                tooltip: TOOLTIP_STYLING
                            }}>
                            <div className="text-sf font-medium pr-2 whitespace-nowrap max-w-[180px] truncate">
                                {logAction.name}
                            </div>
                        </Tooltip>
                    ) : (
                        <div className="text-sf font-medium pr-2 whitespace-nowrap max-w-[180px] truncate">
                            {logAction.name}
                        </div>
                    )}
                    <div className="text-xs font-medium text-sf-gray-500 whitespace-nowrap">{logAction.serverType}</div>
                </div>
                <div className="text-xs font-normal text-sf-gray-500">
                    {parseMillisecondsDate(logAction.lastLogActionDate)}
                </div>
            </div>
        </div>
    );
};

const SentIndicator = () => {
    return (
        <div className="flex gap-1 items-center h-6 pl-1 pr-2 rounded bg-sf-light-100">
            <img src={vIcon} alt="" />
            <div className="text-xs font-medium text-sf-gray-500">Sent</div>
        </div>
    );
};

export const LogWrapper = ({ children }) => {
    return (
        <div className="px-4 py-2 flex justify-between items-center rounded-lg hover:bg-sf-light-100">{children}</div>
    );
};
const SendAllLogsRaw = ({ logAction }) => {
    return (
        <LogWrapper>
            <LogDetails logAction={logAction} icon={AdminConsoleIcon} />
            <SentIndicator />
        </LogWrapper>
    );
};

const SendLogRaw = ({ logAction }) => {
    return (
        <LogWrapper>
            <LogDetails logAction={logAction} icon={fileMediumIcon} />
            <SentIndicator />
        </LogWrapper>
    );
};

const GenerateLogRaw = ({ logAction, onGenerate, onDelete, deleteLogToDelayTime }) => {
    const [hideAfterDelete, setHideAfterDelete] = useState(false);
    const LOGS_ACTIONS = {
        [SERVERS_LOG_TYPES.PUSH_SERVICE]: {
            download: (id) => downloadFile(`/Settings/DownloadPushServerLogs/${id}`),
            generate: (id) =>
                httpPost('/Settings/generateSelectedPushServicesLogs', {
                    selectedPushServices: [id]
                }),
            delete: (id) => httpDelete(`/Settings/DeletePushServerLogs/${id}`)
        },
        [SERVERS_LOG_TYPES.NODE]: {
            download: (id) => downloadFile(`/Settings/DownloadNodeLog/${id}`),
            generate: (id) => httpPost('/Settings/generateSelectedNodesLogs', { selectedNodes: [id] }),
            delete: (id) => httpDelete(`/Settings/DeleteNodeLogs/${id}`)
        },
        [SERVERS_LOG_TYPES.DOMAIN_CONTROLLER]: {
            download: (id, name) => downloadFile(`/Settings/DownloadDCLogs/${id}/${name}`),
            generate: (id) => httpPost('/Settings/generateSelectedDcsLogs', { selectedDcs: [id] }),
            delete: (id) => httpDelete(`/Settings/DeleteDCLogs/${id}`)
        },
        [SERVERS_LOG_TYPES.ADMIN_CONSOLE]: {
            download: () => downloadFile(`/Settings/DownloadServerLogs/tower`),
            generate: () => httpPost('/Settings/generateAdminConsoleLogs'),
            delete: () => httpDelete('/Settings/DeleteTowerLogs')
        }
    };

    useEffect(() => {
        if (deleteLogToDelayTime) {
            const timeForDelete = deleteLogToDelayTime - Date.now();
            if (!timeForDelete) {
                setHideAfterDelete(true);
                return;
            }
            const timerForDelete = setTimeout(() => {
                setHideAfterDelete(true);
            }, timeForDelete);
            return () => {
                clearTimeout(timerForDelete);
            };
        }
    }, [deleteLogToDelayTime]);

    if (hideAfterDelete) {
        return <></>;
    }
    if (deleteLogToDelayTime) {
        return (
            <div className="px-4 py-2 flex justify-between items-center">
                <div className="text-xs font-normal">Deleted from saved logs</div>
                <button
                    className="hover:bg-sf-gray-100 p-2 rounded-lg"
                    onClick={async () => {
                        const result = await httpPost(`/Settings/UndoDeleteLogs/${logAction.id}`);
                        onDelete(result.data.deleteLogsToDelayTime);
                    }}>
                    <span className="flex text-sf">
                        <img className="pr-1" src={undoIcon} alt="" />
                        Undo
                    </span>
                </button>
            </div>
        );
    }

    return (
        <LogWrapper>
            <LogDetails logAction={logAction} icon={fileMediumIcon} />
            <div className="flex gap-1">
                <Tooltip
                    title="Regenerate"
                    enterDelay={1000}
                    enterNextDelay={1000}
                    placement={'top'}
                    classes={{
                        tooltip: TOOLTIP_STYLING
                    }}>
                    <button
                        className="hover:bg-sf-gray-100 p-2 rounded-lg"
                        disabled={logAction.isGenerating}
                        onClick={async () => {
                            const result = await LOGS_ACTIONS[logAction.serverType].generate(logAction.id);
                            onGenerate(result.data.logsToGenerate);
                        }}>
                        <img src={refreshIcon} alt="" />
                    </button>
                </Tooltip>
                <Tooltip
                    title="Download"
                    enterDelay={1000}
                    enterNextDelay={1000}
                    placement={'top'}
                    classes={{
                        tooltip: TOOLTIP_STYLING
                    }}>
                    <button
                        className="hover:bg-sf-gray-100 p-2 rounded-lg"
                        onClick={() => LOGS_ACTIONS[logAction.serverType].download(logAction.id, logAction.name)}>
                        <img src={downloadIcon} alt="" />
                    </button>
                </Tooltip>
                <Tooltip
                    title="Delete"
                    enterDelay={1000}
                    enterNextDelay={1000}
                    placement={'top'}
                    classes={{
                        tooltip: TOOLTIP_STYLING
                    }}>
                    <button
                        className="hover:bg-sf-gray-100 p-2 rounded-lg"
                        onClick={async () => {
                            const result = await LOGS_ACTIONS[logAction.serverType].delete(logAction.id);
                            onDelete(result.data.deleteLogsToDelayTime);
                        }}>
                        <img src={trashMediumIcon} alt="" />
                    </button>
                </Tooltip>
            </div>
        </LogWrapper>
    );
};

const LogManager = ({
    onExitClick,
    pushServices,
    nodes,
    domainControllers,
    adminConsole,
    nodesConfigurations,
    onGenerate,
    onDelete,
    logsToGenerate,
    logsToGenerateInProgress,
    logsToSend,
    logsToSendInProgress,
    logGeneratedNumber,
    logSentNumber,
    deleteLogsToDelayTime,
    sendAllLogsLastSendingDate
}) => {
    const [closeGenerateSuccessfulMsg, setCloseGenerateSuccessfulMsg] = useState(false);
    const [closeSendSuccessfulMsg, setCloseSendSuccessfulMsg] = useState(false);

    useEffect(() => {
        if (logsToGenerate.length) {
            setCloseGenerateSuccessfulMsg(false);
        }
    }, [logsToGenerate]);

    useEffect(() => {
        if (logsToSend.length) {
            setCloseSendSuccessfulMsg(false);
        }
    }, [logsToSend]);

    const wrapperRef = useRef('LogManger');
    useClickOutside(wrapperRef, onExitClick);

    const logsActionsStates = useMemo(() => {
        const logActions = [
            ...pushServices.map((pushService) => {
                return {
                    id: pushService.id,
                    name: pushService.name,
                    action: LOG_ACTION_TYPE.GENERATED,
                    isGeneratingLog: pushService.isGeneratingLog,
                    lastLogActionDate: pushService.lastLogUpload,
                    serverType: SERVERS_LOG_TYPES.PUSH_SERVICE
                };
            }),
            ...pushServices.map((pushService) => {
                return {
                    id: pushService.id,
                    name: pushService.name,
                    action: LOG_ACTION_TYPE.SENT,
                    lastLogActionDate: pushService.lastSendLog,
                    serverType: SERVERS_LOG_TYPES.PUSH_SERVICE
                };
            }),
            ...nodes
                .filter((node) => nodesConfigurations[node.id])
                .map((node) => {
                    return {
                        id: node.id,
                        name: nodesConfigurations[node.id].Name || node.name,
                        action: LOG_ACTION_TYPE.GENERATED,
                        isGeneratingLog: node.isGeneratingLog,
                        lastLogActionDate: parseDateStr(node.LastLog),
                        serverType: SERVERS_LOG_TYPES.NODE
                    };
                }),
            ...nodes
                .filter((node) => nodesConfigurations[node.id])
                .map((node) => {
                    return {
                        id: node.id,
                        name: nodesConfigurations[node.id].Name || node.name,
                        action: LOG_ACTION_TYPE.SENT,
                        lastLogActionDate: node.lastSendLog,
                        serverType: SERVERS_LOG_TYPES.NODE
                    };
                }),
            ...domainControllers.map((dc) => {
                return {
                    id: dc.guid,
                    name: dc.dc_name || dc.display_name,
                    action: LOG_ACTION_TYPE.GENERATED,
                    isGeneratingLog: dc.isGeneratingLog,
                    lastLogActionDate: parseDateStr(dc.lastLogUpload),
                    serverType: SERVERS_LOG_TYPES.DOMAIN_CONTROLLER
                };
            }),
            ...domainControllers.map((dc) => {
                return {
                    id: dc.guid,
                    name: dc.dc_name || dc.display_name,
                    action: LOG_ACTION_TYPE.SENT,
                    lastLogActionDate: dc.lastSendLog,
                    serverType: SERVERS_LOG_TYPES.DOMAIN_CONTROLLER
                };
            }),
            {
                id: TOWER_ID,
                name: SERVERS_LOG_TYPES.ADMIN_CONSOLE,
                action: LOG_ACTION_TYPE.GENERATED,
                lastLogActionDate: parseDateStr(adminConsole.lastLog),
                isGeneratingLog: adminConsole.isGeneratingLog,
                serverType: SERVERS_LOG_TYPES.ADMIN_CONSOLE
            },
            {
                id: TOWER_ID,
                name: SERVERS_LOG_TYPES.ADMIN_CONSOLE,
                action: LOG_ACTION_TYPE.SENT,
                lastLogActionDate: parseDateStr(adminConsole.lastSendLog),
                serverType: SERVERS_LOG_TYPES.ADMIN_CONSOLE
            },
            {
                id: LOG_ACTION_TYPE.SEND_ALL,
                name: LOG_ACTION_TYPE.SEND_ALL,
                action: LOG_ACTION_TYPE.SEND_ALL,
                lastLogActionDate: sendAllLogsLastSendingDate
            }
        ];
        return logActions
            .filter((server) => server.lastLogActionDate)
            .sort((serverA, serverB) => {
                return serverA.lastLogActionDate > serverB.lastLogActionDate ? -1 : 1;
            });
    }, [nodesConfigurations, pushServices, nodes, domainControllers, adminConsole, sendAllLogsLastSendingDate]);

    return (
        <div ref={wrapperRef} className="w-120 bg-white rounded-lg shadow-sf-large">
            <div className="h-16 w-full px-6 flex justify-between items-center border-b border-sf-gray-100">
                <div>Log Manager</div>
                <button onClick={onExitClick} className="hover:bg-sf-gray-100 rounded-lg">
                    <img src={xExitIcon} alt="" />
                </button>
            </div>

            {!!logsToGenerate.length && (
                <div className="h-10 w-full pr-8 pl-8 bg-sf-light-100 border-b border-sf-gray-100 text-xs">
                    <div className="h-full flex items-center gap-2">
                        <img className="animate-spin" src={loadingDarkerIcon} alt="" />
                        <div className="font-medium">Generating...</div>
                        <div className="text-sf-gray-500 font-medium">
                            {`${
                                logsToGenerate.filter(
                                    (log) => !logsToGenerateInProgress.find((logInProgress) => log === logInProgress)
                                ).length
                            } out of ${logsToGenerate.length}`}
                        </div>
                    </div>
                </div>
            )}
            {!logsToGenerate.length && !!logGeneratedNumber && !closeGenerateSuccessfulMsg && (
                <div className="h-10 w-full pr-8 pl-8 bg-sf-light-100 border-b border-sf-gray-100">
                    <div className="h-full flex justify-between items-center">
                        <div className="flex gap-2">
                            <img src={vCircleIcon} alt="" />
                            <div className="text-xs font-medium">
                                Successfully generated logs for {logGeneratedNumber}{' '}
                                {logGeneratedNumber === 1 ? 'component' : 'components'}
                            </div>
                        </div>
                        <button onClick={() => setCloseGenerateSuccessfulMsg(true)} className="text-xs">
                            close
                        </button>
                    </div>
                </div>
            )}
            {!!logsToSend.length && (
                <div className="h-10 w-full pr-8 pl-8 bg-sf-light-100 border-b border-sf-gray-100 text-xs">
                    <div className="h-full flex items-center gap-2">
                        <img className="animate-spin" src={loadingDarkerIcon} alt="" />
                        <div className="font-medium">Sending...</div>
                        <div className="text-sf-gray-500 font-medium">
                            {`${
                                logsToSend.filter(
                                    (log) => !logsToSendInProgress.find((logInProgress) => log === logInProgress)
                                ).length
                            } out of ${logsToSend.length}`}
                        </div>
                    </div>
                </div>
            )}
            {!logsToSend.length && !!logSentNumber && !closeSendSuccessfulMsg && (
                <div className="h-10 w-full pr-8 pl-8 bg-sf-light-100 border-b border-sf-gray-100">
                    <div className="h-full flex justify-between items-center">
                        <div className="flex gap-2">
                            <img src={vCircleIcon} alt="" />
                            <div className="text-xs font-medium">
                                Successfully sent logs for {logSentNumber}{' '}
                                {logSentNumber === 1 ? 'component' : 'components'}
                            </div>
                        </div>
                        <button onClick={() => setCloseSendSuccessfulMsg(true)} className="text-xs">
                            close
                        </button>
                    </div>
                </div>
            )}
            {logsActionsStates.length ? (
                <div className={`max-h-96 p-4 overflow-y-scroll`}>
                    {logsActionsStates.map((logAction) => {
                        if (LOG_ACTION_TYPE.GENERATED === logAction.action) {
                            return (
                                <GenerateLogRaw
                                    key={`${logAction.action}_${logAction.id}`}
                                    logAction={logAction}
                                    onGenerate={onGenerate}
                                    onDelete={onDelete}
                                    deleteLogToDelayTime={deleteLogsToDelayTime[logAction.id]}
                                />
                            );
                        }
                        if (LOG_ACTION_TYPE.SENT === logAction.action) {
                            return <SendLogRaw key={`${logAction.action}_${logAction.id}`} logAction={logAction} />;
                        }
                        if (LOG_ACTION_TYPE.SEND_ALL === logAction.action) {
                            return <SendAllLogsRaw key={`${logAction.action}_${logAction.id}`} logAction={logAction} />;
                        }
                        return <></>;
                    })}
                </div>
            ) : (
                <div className="p-4 h-[328px] text-center">
                    <img src={noLogsIcon} className="ml-auto mr-auto pt-10" alt="" />
                    <div className="text-base">No logs to share</div>
                    <div className="text-xs font-normal text-sf-gray-500">Generate logs for debugging</div>
                </div>
            )}
        </div>
    );
};

export default LogManager;
